import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, Upload, Send } from 'lucide-react';

// Simple notification component
const Notification = ({ message, type, isVisible, onClose }: {
  message: string;
  type: 'success' | 'error';
  isVisible: boolean;
  onClose: () => void;
}) => {
  React.useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg backdrop-blur-md border transform transition-all duration-300 ${
        type === 'success' 
          ? 'bg-violet-600/90 text-zinc-100 border-violet-500/50' 
          : 'bg-red-600/90 text-zinc-100 border-red-500/50'
      } ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
    >
      <p className="font-medium">{message}</p>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const CareersApply = () => {
  const { uniqueId } = useParams<{ uniqueId?: string }>();
  const location = useLocation() as { state?: { position?: string; jobId?: number; questions?: string[] } };
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    coverLetter: '',
    resume: null as File | null,
  });

  const [notification, setNotification] = useState({
    message: '',
    type: 'success' as 'success' | 'error',
    isVisible: false,
  });

  const [questions, setQuestions] = useState<string[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<string[]>([]);
  const [jobId, setJobId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch job data if uniqueId is in URL
  useEffect(() => {
    if (uniqueId) {
      fetchJobByUniqueId(uniqueId);
    } else if (location.state) {
      // Fallback to old method (backward compatibility)
      setFormData(prev => ({ ...prev, position: location.state?.position || '' }));
      const initialQuestions = (location.state?.questions as string[] | undefined) || [];
      setQuestions(initialQuestions);
      setQuestionAnswers(initialQuestions.map(() => ''));
      if (location.state?.jobId) {
        setJobId(location.state.jobId);
      }
    }
  }, [uniqueId, location.state]);

  const fetchJobByUniqueId = async (uid: string) => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/jobs/by-id/${uid}`);
      if (!res.ok) {
        if (res.status === 404) {
          showNotification('Job posting not found or no longer available.', 'error');
        } else {
          throw new Error('Failed to fetch job details');
        }
        return;
      }
      const job = await res.json();
      setFormData(prev => ({ ...prev, position: job.title }));
      setQuestions(Array.isArray(job.questions) ? job.questions : []);
      setQuestionAnswers((Array.isArray(job.questions) ? job.questions : []).map(() => ''));
      setJobId(job.id);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load job details. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type, isVisible: true });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const handleQuestionAnswerChange = (index: number, value: string) => {
    setQuestionAnswers(prev => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, resume: file }));
      showNotification(`Resume uploaded: ${file.name}`);
    }
  };

  // Replace or configure as needed
 const BACKEND_APPLY_URL = (process.env.REACT_APP_BACKEND_SERVER_URL && process.env.REACT_APP_BACKEND_SERVER_URL.trim().length > 0)
   ? process.env.REACT_APP_BACKEND_SERVER_URL
   : 'http://localhost:5000/apply';


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    showNotification("Submitting your application...");

    try {
      const formPayload = new FormData();
      formPayload.append('firstName', formData.firstName);
      formPayload.append('lastName', formData.lastName);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);
      formPayload.append('position', formData.position);
      formPayload.append('coverLetter', formData.coverLetter);
      if (formData.resume) {
        formPayload.append('resume', formData.resume);
      }

      // If there are per-job questions, send structured responses as JSON
      if (questions.length > 0) {
        const responses = questions.map((q, index) => ({
          question: q,
          answer: questionAnswers[index] || '',
        }));
        formPayload.append('questionResponses', JSON.stringify(responses));
      }

      if (!BACKEND_APPLY_URL) {
        throw new Error('Backend URL is not configured. Set REACT_APP_BACKEND_SERVER_URL in frontend/.env');
      }

      const res = await fetch(BACKEND_APPLY_URL, {
        method: 'POST',
        body: formPayload,
      });

      if (!res.ok) {
        throw new Error('Application submission failed.');
      }

      showNotification("Application submitted! We'll review your application and get back to you soon.", 'success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: formData.position, // Keep position
        coverLetter: '',
        resume: null,
      });
      setQuestionAnswers(questions.map(() => '')); // Reset answers but keep questions
    } catch (error: any) {
      showNotification(error.message || 'Something went wrong. Please try again.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
      </div>

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />

      {/* Header Navigation removed as per requirement to only have bottom back button */}

      {/* Main Content */}
      <div className="relative z-10 px-4 md:px-8 py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-zinc-900/40 rounded-xl p-6 md:p-8 border border-zinc-800 shadow-2xl">
            {loading ? (
              <div className="text-center py-12 text-zinc-400">
                Loading job details...
              </div>
            ) : (
              <>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-violet-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 md:w-7 md:h-7 text-zinc-100" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-zinc-100">Apply for a Position</h1>
                <p className="text-zinc-400 text-sm md:text-base">Submit your application and we will get back to you soon.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-zinc-100 mb-2 font-medium">
                    First Name *
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your first name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-zinc-100 mb-2 font-medium">
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your last name"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-zinc-100 mb-2 font-medium">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-zinc-100 mb-2 font-medium">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="position" className="block text-zinc-100 mb-2 font-medium">
                  Position Applying For *
                </label>
                <input
                  id="position"
                  type="text"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300"
                  placeholder="Role title"
                  required
                />
              </div>

              {questions.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-zinc-100 font-semibold">Additional Questions</h2>
                  <p className="text-zinc-400 text-sm mb-2">
                    Please answer the following questions for this role.
                  </p>
                  {questions.map((q, index) => (
                    <div key={index}>
                      <label className="block text-zinc-100 mb-2 font-medium">
                        {q}
                      </label>
                      <textarea
                        value={questionAnswers[index] || ''}
                        onChange={(e) => handleQuestionAnswerChange(index, e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 min-h-[90px] resize-y"
                        placeholder="Your answer"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label htmlFor="resume" className="block text-zinc-100 mb-2 font-medium">
                  Resume/CV *
                </label>
                <div className="relative">
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('resume')?.click()}
                    className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 hover:bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 flex items-center justify-start text-left"
                  >
                    <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                    {formData.resume ? formData.resume.name : 'Upload Resume (PDF, DOC, DOCX)'}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="coverLetter" className="block text-zinc-100 mb-2 font-medium">
                  Cover Letter
                </label>
                <textarea
                  id="coverLetter"
                  value={formData.coverLetter}
                  onChange={(e) => handleInputChange('coverLetter', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800/60 border border-zinc-700/60 rounded-lg text-zinc-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-300 min-h-[120px] resize-y"
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 text-lg bg-violet-600 text-zinc-100 rounded-lg font-medium hover:bg-violet-500 transition-all duration-300 transform shadow-lg flex items-center justify-center"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Application
              </button>
            </form>
                {/* Bottom Back Button */}
                <div className="flex justify-center mt-8">
                  <Link
                    to="/careers"
                    className="inline-flex items-center bg-zinc-800 hover:bg-violet-600 text-zinc-100 px-6 py-3 rounded-md transition-colors duration-200"
                  >
                    <ArrowLeft className="mr-2 w-5 h-5" />
                    <span className="font-medium">Back to Careers</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareersApply;
